import { useState } from 'react'
import {
  useConciergeStore,
  type ChatMode,
  type ModeContext,
} from '@/stores/useConciergeStore'

interface Props {
  /** Which workflow this card triggers when clicked. */
  mode: Exclude<ChatMode, 'default'>
  /** Raw `key=value\n` body of the fenced block. Parsed here. */
  body: string
}

/**
 * Trigger card. Replaces the default code-block rendering for
 *
 *     ```concierge-wrapup
 *     path=/Users/.../<block folder>
 *     name=<display name>
 *     project=<project folder>
 *     output=<the block's output_intent>
 *     ```
 *
 *     ```concierge-prep
 *     path=/Users/.../<block folder>
 *     name=<display name>
 *     project=<project folder>
 *     ```
 *
 *     ```concierge-review
 *     variant=daily|weekly|ninety_day
 *     project=<optional project folder>
 *     ```
 *
 * Clicking the card calls `enterMode(mode, context)`, which swaps the
 * chat shell into the workflow's room and seeds an empty-content first
 * POST so the agent opens with its calibrated greeting.
 *
 * Mirrors `_renderWrapupCards` (and its prep/review siblings) in the
 * Obsidian plugin's main.js — same fenced-block convention so the agent
 * can target both surfaces with one output format.
 */
export function ConciergeWorkflowCard({ mode, body }: Props) {
  const [clicked, setClicked] = useState(false)
  const enterMode = useConciergeStore((s) => s.enterMode)

  const fields = parseFields(body)

  // Malformed (no useful fields)? Render as a fallback pre/code so the
  // agent's intent isn't silently swallowed.
  const hasUsableContext = fields.path || fields.project || fields.variant
  if (!hasUsableContext) {
    return (
      <pre className="concierge-option-card-fallback">
        <code>{body}</code>
      </pre>
    )
  }

  const context: ModeContext = {
    blockPath: fields.path,
    projectPath: fields.project,
    displayTitle: fields.name || undefined,
    reviewVariant:
      mode === 'review'
        ? (fields.variant as ModeContext['reviewVariant']) || undefined
        : undefined,
  }

  const fire = () => {
    if (clicked) return
    setClicked(true)
    enterMode(mode, context)
  }

  const label = CARD_LABEL[mode]
  const subtitle = renderSubtitle(mode, fields)

  return (
    <button
      type="button"
      className="concierge-workflow-card"
      data-mode={mode}
      onClick={fire}
      disabled={clicked}
    >
      <span className="concierge-workflow-card-arrow">→</span>
      <span className="concierge-workflow-card-body">
        <span className="concierge-workflow-card-label">{label}</span>
        {subtitle && (
          <span className="concierge-workflow-card-subtitle">{subtitle}</span>
        )}
        {fields.output && (
          <span className="concierge-workflow-card-output">
            Output: {fields.output}
          </span>
        )}
      </span>
    </button>
  )
}

const CARD_LABEL: Record<Exclude<ChatMode, 'default'>, string> = {
  wrapup: 'Wrap up this block',
  prep: 'Prep this block',
  review: 'Start review',
  // Penn Review is launched from the page header (date-range modal),
  // not via an inline workflow card — but the type system requires
  // every ChatMode have a label. Defensive fallback.
  penn_review: 'Run Penn Review',
}

function renderSubtitle(
  mode: Exclude<ChatMode, 'default'>,
  fields: Record<string, string>
): string {
  if (mode === 'review' && fields.variant) {
    const proj = fields.project ? ` · ${basename(fields.project)}` : ''
    return `${fields.variant.replace('_', ' ')}${proj}`
  }
  if (fields.name) return fields.name
  if (fields.path) return basename(fields.path)
  if (fields.project) return basename(fields.project)
  return ''
}

function basename(p: string): string {
  const parts = p.split('/').filter(Boolean)
  return parts[parts.length - 1] ?? p
}

function parseFields(body: string): Record<string, string> {
  const out: Record<string, string> = {}
  for (const line of body.split('\n')) {
    const eq = line.indexOf('=')
    if (eq === -1) continue
    const k = line.slice(0, eq).trim()
    const v = line.slice(eq + 1).trim()
    if (k) out[k] = v
  }
  return out
}
